// @vendors
const Immutable = require('immutable');
const { get } = require('lodash');
const trim = require('lodash/string/trim');

// @constants
const actionTypes = require('../raConstants/actionTypes');
const { BROKER_GET_MY_INVESTMENTS_SUCCESS } = require('constants/actionTypes');

const initialState = Immutable.fromJS({
    portfolio: {},
    comparative: {},
    isFetching: false,
    error: null,
    patrimony: {
        discretional: {
            amount: 0,
            percentage: 0,
        },
        own: {
            amount: 0,
            percentage: 0,
        },
        total: 0,
    },
    roboAccount: {
        name: '',
        owner: '',
        contract: {
            contractNumber: '',
            productNumber: '',
        },
        ibanComplex: {},
    },
    roboAdvisor: {
        assetAllocation: [],
        keyFigures: {
            valuation: 0,
            cashBalance: 0,
            securitiesValuation: 0,
            investedAmount: 0,
            timeWeightedReturn: 0,
            simpleReturn: 0,
            netReturn: 0,
            netMarketReturn: 0,
            dividends: 0,
            taxes: 0,
            fees: 0,
            maxWithdrawalAmount: 0,
            deposits: 0,
            withdrawals: 0,
        },
    },
    hasRoboHired: false,
    hasRoboCancelled: false,
    shouldShowModifyPlanModal: true,
});

const parseProducts = products => {
    let formattedProducts = {};
    products.forEach(product => {
        formattedProducts[product.product.typeOfProduct] = {
            managementType: product.product.managementType,
            assetAllocations: product.assetAllocations,
        };
    });
    return formattedProducts;
};

const getComparative = (
    state,
    investProfileValue,
    newProfile,
    immOriginalPortfolio,
) => {
    const profile = investProfileValue ? investProfileValue.toString() : '1';
    const newProfilePortfolio = newProfile ? newProfile.toString() : '1';
    const immOriginAssetAllocations = immOriginalPortfolio
        ? immOriginalPortfolio.get('assetAllocation')
        : state.getIn(['portfolio', profile, 'assetAllocations']);
    const immNewAssetAllocations = state.getIn([
        'portfolio',
        newProfilePortfolio,
        'assetAllocations',
    ]);
    const id = `${investProfileValue}-${newProfile}`;
    let comparative = {};
    if (state.getIn(['comparative', id])) {
        return state.getIn(['comparative', id]);
    }

    if (investProfileValue === newProfile) {
        immOriginAssetAllocations.forEach(immAssetAllocations => {
            comparative[immAssetAllocations.get('assetClassId')] = {
                assetClass: immAssetAllocations.get('assetClass'),
                originWeight: immAssetAllocations.get('weight'),
                newWeight: 0,
                subAssetClasses: {},
            };
            const subAssetClassesKey = immAssetAllocations.get(
                'subAssetClasses',
            )
                ? 'subAssetClasses'
                : 'subAssetAllocations';

            immAssetAllocations
                .get(subAssetClassesKey)
                .forEach(immSubAssetClass => {
                    comparative[immAssetAllocations.get('assetClassId')][
                        'subAssetClasses'
                    ][immSubAssetClass.get('subAssetClassId')] = {
                        subAssetClass: immSubAssetClass.get('subAssetClass'),
                        originWeight: immSubAssetClass.get('weight'),
                        newWeight: 0,
                    };
                });
        });

        return comparative;
    }

    immOriginAssetAllocations.forEach(immAssetAllocations => {
        comparative[immAssetAllocations.get('assetClassId')] = {
            assetClass: immAssetAllocations.get('assetClass'),
            originWeight: immAssetAllocations.get('weight'),
            newWeight: 0,
            subAssetClasses: {},
        };
        const subAssetClassesKey = immAssetAllocations.get('subAssetClasses')
            ? 'subAssetClasses'
            : 'subAssetAllocations';

        immAssetAllocations
            .get(subAssetClassesKey)
            .forEach(immSubAssetClass => {
                comparative[immAssetAllocations.get('assetClassId')][
                    'subAssetClasses'
                ][immSubAssetClass.get('subAssetClassId')] = {
                    subAssetClass: immSubAssetClass.get('subAssetClass'),
                    originWeight: immSubAssetClass.get('weight'),
                    newWeight: 0,
                };
            });
    });

    immNewAssetAllocations.forEach(immAssetAllocations => {
        if (
            comparative.hasOwnProperty(immAssetAllocations.get('assetClassId')) // eslint-disable-line
        ) {
            comparative[immAssetAllocations.get('assetClassId')][
                'newWeight'
            ] = immAssetAllocations.get('weight');
            immAssetAllocations
                .get('subAssetClasses')
                .forEach(immSubAssetClass => {
                    if (
                        comparative[immAssetAllocations.get('assetClassId')][
                            'subAssetClasses'
                        ].hasOwnProperty( // eslint-disable-line
                            immSubAssetClass.get('subAssetClassId'),
                        )
                    ) {
                        comparative[immAssetAllocations.get('assetClassId')][
                            'subAssetClasses'
                        ][immSubAssetClass.get('subAssetClassId')][
                            'newWeight'
                        ] = immSubAssetClass.get('weight');
                    } else {
                        comparative[immAssetAllocations.get('assetClassId')][
                            'subAssetClasses'
                        ][immSubAssetClass.get('subAssetClassId')] = {
                            subAssetClass: immSubAssetClass.get(
                                'subAssetClass',
                            ),
                            originWeight: 0,
                            newWeight: immSubAssetClass.get('weight'),
                        };
                    }
                });
        } else {
            comparative[immAssetAllocations.get('assetClassId')] = {
                assetClass: immAssetAllocations.get('assetClass'),
                originWeight: 0,
                newWeight: immAssetAllocations.get('weight'),
                subAssetClasses: {},
            };

            immAssetAllocations
                .get('subAssetClasses')
                .forEach(immSubAssetClass => {
                    comparative[immAssetAllocations.get('assetClassId')][
                        'subAssetClasses'
                    ][immSubAssetClass.get('subAssetClassId')] = {
                        subAssetClass: immSubAssetClass.get('subAssetClass'),
                        originWeight: 0,
                        newWeight: immSubAssetClass.get('weight'),
                    };
                });
        }
    });

    return comparative;
};

const formatAccount = account => {
    if (!account) {
        return false;
    }
    const ibanComplex = {
        codbban: trim(get(account, 'codIban.codbban', '')),
        digitodecontrol: get(account, 'codIban.digitodecontrol', ''),
        pais: get(account, 'codIban.pais', ''),
    };
    return {
        name: account.descripcion,
        owner: trim(account.nombretitular),
        contract: {
            contractNumber: get(account, 'contrato.numeroContrato'),
            productNumber: get(account, 'contrato.producto'),
        },
        ibanComplex,
    };
};

const RAPortfolioDistribution = (state = initialState, action) => {
    let comparativeId;
    switch (action.type) {
        case actionTypes.RA_PORTFOLIO_DISTRIBUTION_CANCEL_MODIFY_PLAN_MODAL:
            return state.merge({
                shouldShowModifyPlanModal: false,
            });
        case actionTypes.RA_PORTFOLIO_DISTRIBUTION_GET_DISTRIBUTION_REQUEST:
            return state.merge({
                isFetching: true,
                error: false,
            });
        case actionTypes.RA_PORTFOLIO_DISTRIBUTION_GET_DISTRIBUTION_SUCCESS:
            return state.mergeDeep({
                portfolio: parseProducts(action.payload.portfolio),
                isFetching: false,
            });
        case actionTypes.RA_PORTFOLIO_DISTRIBUTION_GET_DISTRIBUTION_FAILURE:
            return state.merge({
                error: action.payload.error,
                isFetching: false,
            });
        case actionTypes.RA_PORTFOLIO_DISTRIBUTION_RESET_COMPARATIVE:
            const profile = action.payload.portfolioId;
            const immOriginalPortfolio = action.payload.originalPortfolio;
            comparativeId = `${profile}-${profile}`;
            return state.merge({
                comparative: {
                    [comparativeId]: getComparative(
                        state,
                        profile,
                        profile,
                        immOriginalPortfolio,
                    ),
                },
            });
        case actionTypes.RA_PORTFOLIO_DISTRIBUTION_RESET_PORTFOLIO:
            return initialState;
        case actionTypes.RA_MANAGEMENT_PLAN_MODIFY_STRATEGY_AND_PROFILE:
        case actionTypes.RA_MANAGEMENT_PLAN_MODIFY_PROFILE:
            const {
                investProfileValue,
                newProfile,
                originalPortfolio,
            } = action.payload;
            comparativeId = `${investProfileValue}-${newProfile}`;
            return state.mergeDeep({
                comparative: {
                    [comparativeId]: getComparative(
                        state,
                        investProfileValue,
                        newProfile,
                        originalPortfolio,
                    ),
                },
            });
        case BROKER_GET_MY_INVESTMENTS_SUCCESS:
            const { roboAdvisor, patrimony, roboAccount } = action.payload;
            const hasRoboActive = get(
                roboAdvisor,
                'activeCustomerTotal',
                false,
            );
            const hasRoboHired = !!roboAdvisor && hasRoboActive;
            return state
                .mergeDeep({
                    patrimony,
                    hasRoboHired,
                    hasRoboCancelled: get(
                        roboAdvisor,
                        'state.cancelled',
                        false,
                    ),
                    hasRoboActive,
                    roboAccount:
                        formatAccount(roboAccount) ||
                        initialState.get('roboAccount'),
                })
                .set(
                    'roboAdvisor',
                    hasRoboHired
                        ? Immutable.fromJS(roboAdvisor)
                        : initialState.get('roboAdvisor'),
                );
        default:
            return state;
    }
};

module.exports = RAPortfolioDistribution;
